/**
 * WHAT A TARGETED STATE ON A SORT FIRES ON.
 *
 * Three structural patterns the platform recognises with nothing to author, plus the one this
 * block exists for: **a single item in a single bin**.
 *
 * That last one is a PARTIAL condition — "X ended up in Y, never mind the rest" — and it is the
 * difference between a block and a widget. The source activity this block was built from graded as
 * one boolean, so it could say "wrong" and never *"you filed 'civilizations are unstable' under
 * Observations"*, which is the entire teachable moment. Two pickers, added on demand. An N x K
 * matrix never appears anywhere: that is a combo grid, which is ruled out directly.
 *
 * Custom whole-arrangement triggers are supported by the evaluator and tested
 * (`buildSortPartitionTrigger`), but are deliberately NOT offered here yet — authoring one needs a
 * place to lay out a whole wrong arrangement, and a kind in the picker with no editor behind it is
 * worse than one that is not offered.
 */
import { ORDER_ANY_BIN, SORT_STRUCTURAL_TRIGGERS, availableSortStructuralTriggers, buildSortItemTrigger, buildSortOrderTrigger, buildSortPartitionTrigger, parseSortItemTrigger, parseSortOrderTrigger, parseSortPartitionTrigger } from '@agavi/blocks';
import { Trans, useTranslation } from 'react-i18next';

import { TriggerDropdown } from './trigger-dropdown';

import type { SortBin, SortItem } from '@agavi/blocks';

import SelectMenu from '@/components/ui/select-menu';
import { authoredPreviewNode } from '@/lib/block-condition-chips';
import { sortBinLabelText, sortItemLabelText, sortTargetedLabel } from '@/lib/block-state-labels';

type TFn = (key: string, opts?: Record<string, unknown>) => string;

export type SortTriggerKind = (typeof SORT_STRUCTURAL_TRIGGERS)[number] | 'item' | 'order' | 'custom';

export function sortTriggerKind(trigger: string): SortTriggerKind {
  if ((SORT_STRUCTURAL_TRIGGERS as readonly string[]).includes(trigger)) return trigger as SortTriggerKind;
  if (parseSortPartitionTrigger(trigger) !== null) return 'custom';
  return parseSortOrderTrigger(trigger) !== null ? 'order' : 'item';
}

/** An item by what it says, or by its position when it says nothing yet. A blank rich-text field
 *  serialises to `<p></p>`, so the fallback has to test the STRIPPED text, not the raw markup. */
/* RE-EXPORTED, not reimplemented. The derivation lives in `lib/block-state-labels` because Flow
   renders a state's label through that module and `lib/` cannot import a component — so a copy
   beside the editor is a copy Flow cannot reach, and the two drift. That drift is the exact
   failure (one GPS state rendering three ways at once) the shared module was created to end. */
export const sortItemLabel = (items: SortItem[], id: string, t: TFn, itemsAsEquations = false) =>
  sortItemLabelText(items, id, t, itemsAsEquations);
export const sortBinLabel = (bins: SortBin[], id: string, t: TFn) => sortBinLabelText(bins, id, t);

const STRUCTURAL_LABEL_KEYS: Record<string, string> = {
  all_in_one_bin: 'blocks.sort.triggerAllInOneBin',
  bins_swapped: 'blocks.sort.triggerBinsSwapped',
  one_misfiled: 'blocks.sort.triggerOneMisfiled',
};

/** The title, derived mechanically and never typed — through the ONE derivation above. */
export function sortTargetedTitle(
  trigger: string, items: SortItem[], bins: SortBin[], t: TFn, itemsAsEquations = false,
): string {
  return sortTargetedLabel(trigger, { items, bins, itemsAsEquations }, t) ?? t('blocks.sort.triggerUnknown');
}

export function nextSortTrigger(used: Set<string>, items: SortItem[], bins: SortBin[]): string {
  const structural = availableSortStructuralTriggers(bins).find(k => !used.has(k));
  if (structural) return structural;
  for (const item of items) {
    for (const bin of bins) {
      const candidate = buildSortItemTrigger(item.id, bin.id);
      if (!used.has(candidate)) return candidate;
    }
  }
  return buildSortItemTrigger(items[0]?.id ?? '', bins[0]?.id ?? '');
}

/**
 * The expanded editor.
 *
 * A structural kind names exactly one arrangement, so a second state claiming the same one would
 * fire identically with the first and never be a distinct condition — `usedKinds` takes those off
 * the menu. `item` is exempt, because it is parameterised by WHICH item and WHICH bin, so several
 * are the normal pattern rather than a duplicate.
 */
export function SortTriggerEditor({ trigger, onChange, items, bins, usedKinds, orderGraded, itemsAsEquations = false, evaluation }: {
  trigger: string;
  onChange: (trigger: string) => void;
  items: SortItem[];
  bins: SortBin[];
  usedKinds: SortTriggerKind[];
  /** Enforce Order, for the version in force. */
  orderGraded: boolean;
  /** Names an item by its raw formula in the picker, as the chips and titles do. */
  itemsAsEquations?: boolean;
  /** The CARD's verdict, which the kind picker wears — see the `TriggerDropdown` beside it. */
  evaluation?: 'correct' | 'incorrect' | 'neutral';
}) {
  const { t } = useTranslation();
  const kind = sortTriggerKind(trigger);
  const parsed = parseSortItemTrigger(trigger);
  const itemId = parsed?.itemId ?? items[0]?.id ?? '';
  const binId = parsed?.binId ?? bins[0]?.id ?? '';
  const orderBin = parseSortOrderTrigger(trigger) ?? ORDER_ANY_BIN;

  /* OFFERED ONLY WHILE ORDER IS GRADED, the way `bins_swapped` is offered only at two bins: the
     mistake it names does not exist when any stacking of the right items is correct, so a card
     authored against it could never fire. Kept in the list when it is the CURRENT value, so
     turning the switch off greys a card rather than silently rewriting its condition. */
  const kinds: SortTriggerKind[] = [
    ...availableSortStructuralTriggers(bins),
    ...(orderGraded || kind === 'order' ? ['order' as const] : []),
    'item',
    /* A WHOLE AUTHORED ARRANGEMENT, last in the list because it is the most specific thing a
       teacher can say and the structural patterns cover the common mistakes above it. */
    'custom',
  ];
  /* `item` and `custom` are both repeatable — a teacher may name several items, and several whole
     arrangements — so neither is spent by being used once. */
  const offered = kinds.filter(k => k === 'item' || k === 'custom' || k === kind || !usedKinds.includes(k));

  /**
   * THE ARRANGEMENT BEING AUTHORED, seeded from the trigger when it holds one.
   *
   * Every item must sit somewhere: an unplaced item makes the attempt Incomplete, which is a
   * structural gate that fires before correctness is even attempted, so an arrangement with a gap
   * in it could never match anything. Items the stored trigger does not mention fall into the
   * first bin, which is the only honest default — a blank cannot be stored.
   */
  const stored = parseSortPartitionTrigger(trigger) ?? {};
  const binOf = (itemId: string) =>
    Object.entries(stored).find(([, ids]) => ids.includes(itemId))?.[0] ?? bins[0]?.id ?? '';
  const arrangement: Record<string, string[]> = {};
  for (const item of items) {
    const b = binOf(item.id);
    (arrangement[b] ??= []).push(item.id);
  }
  const withItemMoved = (itemId: string, toBin: string) => {
    const next: Record<string, string[]> = {};
    for (const [binId, ids] of Object.entries(arrangement)) next[binId] = ids.filter(id => id !== itemId);
    (next[toBin] ??= []).push(itemId);
    return next;
  };

  return (
    /* THE APP'S OWN MENU, not a native `<select>`. Both exist and both are deliberate, but a
       native select opens the OPERATING SYSTEM's menu — and beside the other trigger pickers that
       is visibly a different control in a panel otherwise entirely Agavi. `trigger-kind.tsx` makes
       the same choice and says so; this followed `SelectField` instead and looked foreign. */
    <div className='flex min-w-0 flex-wrap items-center gap-2'>
      {/* THE SHARED FEEDBACK PICKER for the KIND, which is the one choice that carries the card's
          verdict. Sort is the card-driven pole, like Ordering: "one item misfiled" has no
          correctness of its own, so the tone and symbol follow the evaluation the teacher cycles.
          The item and bin pickers beside it are PARAMETERS of the condition, not verdicts, so they
          stay plain menus — three chips in a row would each claim to be the verdict. */}
      <TriggerDropdown
        value={kind}
        ariaLabel={t('blocks.sort.triggerKindLabel')}
        evaluation={evaluation}
        onChange={(next) => onChange(
          next === 'item' ? buildSortItemTrigger(itemId, binId)
            : next === 'order' ? buildSortOrderTrigger(orderBin)
              : next === 'custom' ? buildSortPartitionTrigger(arrangement)
                : next,
        )}
        options={offered.map(k => ({
          value: k,
          label: k === 'item' ? t('blocks.sort.triggerItemInBin')
            : k === 'order' ? t('blocks.sort.triggerWrongOrder')
              : k === 'custom' ? t('blocks.sort.triggerCustomArrangement')
                : t(STRUCTURAL_LABEL_KEYS[k]!),
        }))}
      />
      {kind === 'order' && (
        /* One bin, or ALL of them — a sort where order matters throughout needs one card, not
           one per bin. */
        <SelectMenu
          fieldId={`${trigger}.orderBin`}
          ariaLabel={t('blocks.sort.triggerBinLabel')}
          value={orderBin}
          onChange={(next) => onChange(buildSortOrderTrigger(next))}
          options={[
            { value: ORDER_ANY_BIN, label: t('blocks.sort.triggerAnyBin') },
            ...bins.map(b => ({ value: b.id, label: sortBinLabel(bins, b.id, t) })),
          ]}
        />
      )}
      {kind === 'custom' && (
        /* ONE ROW PER ITEM, each naming the bin it sits in for this arrangement.
           A second drag surface was the obvious alternative and the wrong one: the bins editor
           above already owns dragging, a feedback card is a narrow column, and a teacher naming a
           wrong arrangement is making N discrete statements rather than performing a rearrangement.
           Pickers are also the keyboard-reachable form, which a drag list is not. */
        <ul className='flex w-full min-w-0 flex-col gap-1.5'>
          {items.map(item => (
            <li key={item.id} className='flex min-w-0 items-center gap-2'>
              <span className='min-w-0 flex-1 truncate text-xs text-ws-text'>
                {authoredPreviewNode({
                  media: item.media as { kind: 'image' | 'audio' } | undefined,
                  text: item.text ?? '',
                  formula: itemsAsEquations ? (item.formula ?? '') : undefined,
                }) ?? sortItemLabel(items, item.id, t, itemsAsEquations)}
              </span>
              <SelectMenu
                fieldId={`${trigger}.arrangement.${item.id}`}
                ariaLabel={t('blocks.sort.triggerBinLabel')}
                value={binOf(item.id)}
                onChange={(next) => onChange(buildSortPartitionTrigger(withItemMoved(item.id, next)))}
                options={bins.map(b => ({
                  value: b.id,
                  label: authoredPreviewNode({ text: b.label ?? '' }) ?? sortBinLabel(bins, b.id, t),
                }))}
              />
            </li>
          ))}
        </ul>
      )}
      {kind === 'item' && (
        /* A SENTENCE, not two loose controls: "[Trilobite] in [Paleozoic]". The relationship is the
           whole condition, and two bare dropdowns side by side do not say which way round it runs.
           `inline` is the menu's own variant for a dropdown that is a WORD in a sentence — the
           bordered default is also far too heavy for a feedback card's narrow column. The
           connective comes from the same `<Trans>` template the chip uses, so the word and its
           position stay with the translation. */
        <span className='inline-flex min-w-0 flex-wrap items-center gap-1 text-xs text-ws-text'>
          <Trans
            i18nKey='blocks.sort.chipItemInBinRich'
            components={{
              item: (
                <SelectMenu
                  inline
                  fieldId={`${trigger}.item`}
                  ariaLabel={t('blocks.sort.triggerItemLabel')}
                  value={itemId}
                  onChange={(next) => onChange(buildSortItemTrigger(next, binId))}
                  options={items.map(i => ({
                    value: i.id,
                    label: authoredPreviewNode({
                      media: i.media as { kind: 'image' | 'audio' } | undefined,
                      text: i.text ?? '',
                      formula: itemsAsEquations ? (i.formula ?? '') : undefined,
                    }) ?? sortItemLabel(items, i.id, t, itemsAsEquations),
                  }))}
                />
              ),
              bin: (
                <SelectMenu
                  inline
                  fieldId={`${trigger}.bin`}
                  ariaLabel={t('blocks.sort.triggerBinLabel')}
                  value={binId}
                  onChange={(next) => onChange(buildSortItemTrigger(itemId, next))}
                  options={bins.map(b => ({
                    value: b.id,
                    label: authoredPreviewNode({ text: b.label ?? '' }) ?? sortBinLabel(bins, b.id, t),
                  }))}
                />
              ),
            }}
          />
        </span>
      )}
    </div>
  );
}
